import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle, XCircle, Award, Download, RotateCcw, Loader2 } from 'lucide-react';
import { markQuizCompleted, getActivityProgress, loadProgressFromBackend } from '../utils/progressTracker';

const QUIZ_ACTIVITY_ID = 'formation-langage-0-3-quiz';

const questions = [
  {
    q: "À quel âge apparaît généralement le babillage chez l'enfant ?",
    options: ["0 à 3 mois", "6 à 12 mois", "12 à 18 mois", "18 à 24 mois"],
    answer: 1,
    explanation: "Le babillage (ba-ba, ma-ma...) apparaît entre 6 et 12 mois, accompagné de la réaction au prénom et du pointage."
  },
  {
    q: "Quel est le facteur le plus important pour le développement du langage ?",
    options: ["Les écrans éducatifs", "La qualité des interactions adulte-enfant", "La quantité de jouets", "La génétique uniquement"],
    answer: 1,
    explanation: "Les interactions de qualité (regard, disponibilité, tour de rôle) sont le facteur le plus déterminant pour le développement langagier."
  },
  {
    q: "Pourquoi les écrans avant 3 ans sont-ils considérés comme un frein au langage ?",
    options: ["Ils abîment les yeux", "Ils réduisent les interactions adultes-enfants", "Ils sont trop bruyants", "Ils empêchent le sommeil"],
    answer: 1,
    explanation: "Les écrans réduisent les interactions adultes-enfants, diminuent l'attention conjointe et le tour de rôle, qui sont essentiels au développement du langage."
  },
  {
    q: "Que signifie « l'explosion lexicale » chez l'enfant ?",
    options: ["L'enfant crie beaucoup", "L'augmentation rapide du vocabulaire entre 12 et 24 mois", "L'enfant invente des mots", "L'enfant parle dans plusieurs langues"],
    answer: 1,
    explanation: "L'explosion lexicale désigne l'augmentation rapide du vocabulaire qui survient entre 12 et 24 mois, passant de quelques mots à plusieurs dizaines."
  },
  {
    q: "Quel signe d'alerte doit attirer l'attention vers 18 mois ?",
    options: ["L'enfant ne marche pas", "Aucun mot et pas de pointage", "L'enfant ne mange pas seul", "L'enfant dort mal"],
    answer: 1,
    explanation: "L'absence de tout mot et de pointage vers 18 mois constitue un signe d'alerte qui justifie une observation attentive et un échange avec les parents."
  },
  {
    q: "Quelle est la bonne attitude quand un enfant fait une erreur de langage ?",
    options: ["Le corriger fermement", "L'ignorer", "Reformuler correctement sans corriger brutalement", "Lui demander de répéter jusqu'à ce qu'il dise bien"],
    answer: 2,
    explanation: "Il faut reformuler sans corriger brutalement : si l'enfant dit « moi veut eau », répondre « Tu veux de l'eau ? Oui, je te donne de l'eau. » Cela valorise sa tentative tout en lui offrant le modèle correct."
  },
  {
    q: "Que signifie « l'attention conjointe » ?",
    options: ["Être attentif en classe", "Regarder la même chose que l'enfant en même temps", "Faire attention aux dangers", "Écouter de la musique ensemble"],
    answer: 1,
    explanation: "L'attention conjointe consiste à regarder le même objet ou la même scène que l'enfant en même temps, ce qui favorise considérablement le développement du langage."
  },
  {
    q: "À quel âge l'enfant commence-t-il à utiliser le « je » ?",
    options: ["Avant 1 an", "Vers 12-18 mois", "Entre 2 et 3 ans", "Après 4 ans"],
    answer: 2,
    explanation: "L'utilisation du « je » apparaît entre 2 et 3 ans, en même temps que la structuration des phrases et le début de la narration."
  },
  {
    q: "Que signifie TDL ?",
    options: ["Trouble de la Diction Lente", "Trouble Développemental du Langage", "Test de Développement Langagier", "Technique de Dialogue Libre"],
    answer: 1,
    explanation: "Le TDL (Trouble Développemental du Langage) est une difficulté durable de compréhension et/ou d'expression du langage, qui nécessite un suivi spécialisé."
  },
  {
    q: "Parmi ces activités, laquelle favorise le plus le développement du langage chez le jeune enfant ?",
    options: ["Regarder des dessins animés", "La lecture interactive avec échanges autour des images", "Jouer seul avec des cubes", "Écouter de la musique classique"],
    answer: 1,
    explanation: "La lecture interactive avec des échanges (« Où est... ? », « Qu'est-ce que tu vois ? ») favorise le vocabulaire, la compréhension et le lien affectif."
  }
];

function Certificate({ name, score, total, date, title }) {
  const certRef = useRef(null);
  const pct = Math.round((score / total) * 100);

  const handlePrint = () => {
    const content = certRef.current;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Attestation de réussite</title>
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Inter:wght@400;600&display=swap');
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { display: flex; justify-content: center; align-items: center; min-height: 100vh; background: #f5f5f5; }
        @media print { body { background: white; } .no-print { display: none; } }
      </style></head><body>
      ${content.outerHTML}
      <div class="no-print" style="text-align:center;margin-top:20px">
        <button onclick="window.print()" style="padding:12px 32px;font-size:16px;background:#0f3460;color:white;border:none;border-radius:8px;cursor:pointer">Imprimer / Télécharger PDF</button>
      </div>
      </body></html>
    `);
    win.document.close();
  };

  return (
    <div>
      <div ref={certRef} style={{
        width: '800px', maxWidth: '100%', margin: '0 auto', padding: '3rem',
        background: 'white', border: '3px solid #0f3460', borderRadius: '0',
        position: 'relative', fontFamily: "'Inter', sans-serif"
      }}>
        {/* Inner border */}
        <div style={{ border: '1px solid #C9A96E', padding: '2.5rem', position: 'relative' }}>
          {/* Corner accents */}
          {[{ top: 0, left: 0 }, { top: 0, right: 0 }, { bottom: 0, left: 0 }, { bottom: 0, right: 0 }].map((pos, i) => (
            <div key={i} style={{ position: 'absolute', ...pos, width: '30px', height: '30px', borderColor: '#C9A96E', borderStyle: 'solid', borderWidth: 0, ...(pos.top !== undefined ? { borderTopWidth: '2px' } : { borderBottomWidth: '2px' }), ...(pos.left !== undefined ? { borderLeftWidth: '2px' } : { borderRightWidth: '2px' }) }} />
          ))}

          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#C9A96E', fontSize: '0.8rem', letterSpacing: '0.3em', fontWeight: '600', marginBottom: '0.5rem' }}>
              APDR DE LA VAE
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2.2rem', color: '#0f3460', marginBottom: '0.5rem', fontWeight: '700' }}>
              Attestation de Réussite
            </h1>
            <div style={{ width: '80px', height: '3px', background: 'linear-gradient(90deg, #C9A96E, #0f3460, #C9A96E)', margin: '1rem auto 2rem' }} />

            <p style={{ color: '#6B7280', fontSize: '0.95rem', marginBottom: '0.5rem' }}>Ce document certifie que</p>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.8rem', color: '#1F2937', marginBottom: '1.5rem', fontWeight: '700' }}>
              {name}
            </h2>

            <p style={{ color: '#6B7280', fontSize: '0.95rem', marginBottom: '0.5rem' }}>a complété avec succès la formation</p>
            <h3 style={{ fontSize: '1.3rem', color: '#0f3460', fontWeight: '700', marginBottom: '1.5rem', padding: '0.75rem 1.5rem', background: '#EEF2FF', display: 'inline-block', borderRadius: '0.5rem' }}>
              {title}
            </h3>

            <div style={{ display: 'flex', justifyContent: 'center', gap: '3rem', margin: '2rem 0' }}>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#059669' }}>{pct}%</div>
                <div style={{ fontSize: '0.8rem', color: '#6B7280' }}>Score obtenu</div>
              </div>
              <div>
                <div style={{ fontSize: '2rem', fontWeight: '800', color: '#0f3460' }}>{score}/{total}</div>
                <div style={{ fontSize: '0.8rem', color: '#6B7280' }}>Réponses correctes</div>
              </div>
            </div>

            <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '0.8rem', color: '#6B7280' }}>Date de délivrance</div>
                <div style={{ fontWeight: '600', color: '#1F2937' }}>{date}</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '1.1rem', color: '#0f3460', fontStyle: 'italic' }}>
                  APDR de la VAE
                </div>
                <div style={{ fontSize: '0.8rem', color: '#6B7280', marginTop: '0.25rem' }}>Organisme de formation</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <button onClick={handlePrint} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
          <Download size={18} /> Imprimer / Télécharger l'attestation
        </button>
      </div>
    </div>
  );
}

export default function FormationLangageQuiz({ userName }) {
  const [currentQ, setCurrentQ] = useState(0);
  const [answers, setAnswers] = useState(Array(questions.length).fill(null));
  const [showResult, setShowResult] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savedResult, setSavedResult] = useState(null);

  // Load previous quiz result on mount
  useEffect(() => {
    const loadPrevious = async () => {
      try {
        await loadProgressFromBackend();
        const progress = getActivityProgress(QUIZ_ACTIVITY_ID);
        if (progress && progress.quizCompleted && progress.quizScore >= 7) {
          setSavedResult({
            score: progress.quizScore,
            total: progress.quizTotalQuestions || questions.length,
            date: progress.quizCompletedAt
              ? new Date(progress.quizCompletedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
              : new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
          });
        }
      } catch {}
      setLoading(false);
    };
    loadPrevious();
  }, []);

  const q = questions[currentQ];
  const score = answers.filter((a, i) => a === questions[i].answer).length;
  const pct = Math.round((score / questions.length) * 100);
  const passed = score >= 7;

  const handleSelect = (idx) => {
    if (answers[currentQ] !== null) return;
    setSelectedOption(idx);
    const newAnswers = [...answers];
    newAnswers[currentQ] = idx;
    setAnswers(newAnswers);
    setShowExplanation(true);
  };

  const handleNext = () => {
    setShowExplanation(false);
    setSelectedOption(null);
    if (currentQ < questions.length - 1) {
      setCurrentQ(currentQ + 1);
    } else {
      // Quiz finished — save to backend
      const finalScore = answers.reduce((acc, a, i) => acc + (a === questions[i].answer ? 1 : 0), 0);
      markQuizCompleted(QUIZ_ACTIVITY_ID, finalScore, questions.length);
      if (finalScore >= 7) {
        setSavedResult({
          score: finalScore,
          total: questions.length,
          date: new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
        });
      }
      setShowResult(true);
    }
  };

  const handleRetry = () => {
    setAnswers(Array(questions.length).fill(null));
    setCurrentQ(0);
    setShowResult(false);
    setShowExplanation(false);
    setSelectedOption(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '3rem' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: '#0f3460', margin: '0 auto' }} />
        <p style={{ color: '#6B7280', marginTop: '0.5rem' }}>Chargement...</p>
      </div>
    );
  }

  // If already passed — show certificate directly
  if (savedResult && !showResult) {
    const savedPct = Math.round((savedResult.score / savedResult.total) * 100);
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div className="card" style={{ padding: '2rem', textAlign: 'center', marginBottom: '2rem' }}>
          <Award size={64} color="#059669" style={{ margin: '0 auto 1rem' }} />
          <h2 data-testid="quiz-already-passed" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#059669', marginBottom: '0.5rem' }}>
            Quiz validé !
          </h2>
          <p style={{ fontSize: '1.1rem', color: '#6B7280', marginBottom: '0.5rem' }}>
            Vous avez obtenu <strong>{savedResult.score}/{savedResult.total}</strong> ({savedPct}%)
          </p>
          <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
            Votre attestation de réussite est disponible ci-dessous.
          </p>
        </div>
        <Certificate
          name={userName || 'Candidat(e)'}
          score={savedResult.score}
          total={savedResult.total}
          date={savedResult.date}
          title="Développement du langage (0-3 ans)"
        />
      </div>
    );
  }

  if (showResult) {
    const today = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        <div className="card" style={{ padding: '2rem', textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
            {passed ? <Award size={64} color="#059669" /> : <RotateCcw size={64} color="#DC2626" />}
          </div>
          <h2 data-testid="quiz-result-title" style={{ fontSize: '1.8rem', fontWeight: 'bold', color: passed ? '#059669' : '#DC2626', marginBottom: '0.5rem' }}>
            {passed ? 'Félicitations !' : 'Presque...'}
          </h2>
          <p style={{ fontSize: '1.1rem', color: '#6B7280', marginBottom: '1rem' }}>
            Vous avez obtenu <strong>{score}/{questions.length}</strong> ({pct}%)
          </p>
          <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
            {passed
              ? 'Vous avez réussi le quiz ! Votre attestation de réussite est disponible ci-dessous.'
              : 'Il faut obtenir au moins 7/10 pour valider la formation. Réessayez !'}
          </p>
          {!passed && (
            <button onClick={handleRetry} data-testid="quiz-retry-btn" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              <RotateCcw size={18} /> Réessayer le quiz
            </button>
          )}
        </div>

        {passed && (
          <Certificate
            name={userName || 'Candidat(e)'}
            score={score}
            total={questions.length}
            date={today}
            title="Développement du langage (0-3 ans)"
          />
        )}

        {/* Recap answers */}
        <div style={{ marginTop: '2rem' }}>
          <h3 style={{ fontWeight: '700', marginBottom: '1rem' }}>Récapitulatif des réponses</h3>
          {questions.map((question, i) => (
            <div key={i} className="card" style={{ padding: '1rem', marginBottom: '0.75rem', borderLeft: `3px solid ${answers[i] === question.answer ? '#10B981' : '#EF4444'}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                {answers[i] === question.answer
                  ? <CheckCircle size={20} color="#10B981" style={{ flexShrink: 0, marginTop: '2px' }} />
                  : <XCircle size={20} color="#EF4444" style={{ flexShrink: 0, marginTop: '2px' }} />}
                <div>
                  <p style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.25rem' }}>{question.q}</p>
                  <p style={{ fontSize: '0.85rem', color: '#6B7280' }}>
                    Votre réponse : <strong>{question.options[answers[i]]}</strong>
                    {answers[i] !== question.answer && <> — Bonne réponse : <strong style={{ color: '#10B981' }}>{question.options[question.answer]}</strong></>}
                  </p>
                  <p style={{ fontSize: '0.8rem', color: '#6B7280', fontStyle: 'italic', marginTop: '0.25rem' }}>{question.explanation}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto' }}>
      {/* Progress */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
        <span style={{ fontWeight: '700', color: '#0f3460', fontSize: '0.9rem' }}>Question {currentQ + 1}/{questions.length}</span>
        <div style={{ flex: 1, height: '6px', background: '#E5E7EB', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ width: `${((currentQ + 1) / questions.length) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #0f3460, #3B82F6)', borderRadius: '3px', transition: 'width 0.3s' }} />
        </div>
      </div>

      <div className="card" style={{ padding: '2rem', marginBottom: '1.5rem' }}>
        <h3 style={{ fontSize: '1.15rem', fontWeight: '700', color: '#1F2937', marginBottom: '1.5rem', lineHeight: 1.4 }}>
          {q.q}
        </h3>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {q.options.map((opt, idx) => {
            const isSelected = selectedOption === idx;
            const isCorrect = idx === q.answer;
            const answered = answers[currentQ] !== null;
            let bg = 'white';
            let border = '1px solid #D1D5DB';
            let color = '#374151';
            if (answered) {
              if (isCorrect) { bg = '#ECFDF5'; border = '2px solid #10B981'; color = '#065F46'; }
              else if (isSelected && !isCorrect) { bg = '#FEF2F2'; border = '2px solid #EF4444'; color = '#991B1B'; }
            } else if (isSelected) {
              bg = '#EEF2FF'; border = '2px solid #3B82F6';
            }

            return (
              <button
                key={idx}
                data-testid={`quiz-option-${idx}`}
                onClick={() => handleSelect(idx)}
                disabled={answered}
                style={{
                  display: 'flex', alignItems: 'center', gap: '1rem',
                  padding: '1rem 1.25rem', borderRadius: '0.75rem',
                  background: bg, border, color,
                  cursor: answered ? 'default' : 'pointer',
                  textAlign: 'left', fontSize: '0.95rem', lineHeight: 1.4,
                  transition: 'all 0.2s'
                }}
              >
                <span style={{
                  width: '32px', height: '32px', borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: '700', fontSize: '0.85rem',
                  background: answered && isCorrect ? '#10B981' : answered && isSelected ? '#EF4444' : '#F3F4F6',
                  color: answered && (isCorrect || isSelected) ? 'white' : '#6B7280'
                }}>
                  {String.fromCharCode(65 + idx)}
                </span>
                {opt}
                {answered && isCorrect && <CheckCircle size={20} color="#10B981" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                {answered && isSelected && !isCorrect && <XCircle size={20} color="#EF4444" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
              </button>
            );
          })}
        </div>

        {/* Explanation */}
        {showExplanation && (
          <div style={{ marginTop: '1.5rem', padding: '1rem 1.25rem', background: '#F0F9FF', borderRadius: '0.75rem', border: '1px solid #BAE6FD' }}>
            <p style={{ fontWeight: '600', color: '#0369A1', fontSize: '0.85rem', marginBottom: '0.25rem' }}>Explication</p>
            <p style={{ color: '#374151', fontSize: '0.9rem', lineHeight: 1.5 }}>{q.explanation}</p>
          </div>
        )}
      </div>

      {showExplanation && (
        <div style={{ textAlign: 'right' }}>
          <button onClick={handleNext} data-testid="quiz-next-btn" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            {currentQ < questions.length - 1 ? 'Question suivante →' : 'Voir les résultats'}
          </button>
        </div>
      )}
    </div>
  );
}
